import React, { useEffect } from "react";
import { Dropdown } from "react-bootstrap";
import MerchantAccountStore from "../MerchantAccountStore";
import { observer } from "mobx-react";
import { MerchantCard } from "./Merchant.styles";
import MindMePaymentsLogo from "../../../../../../images/logos/mindme-payment-logo.svg";
import ellipsisVertical from "../../../../../../images/common/ellipsis-vertical-royal-blue.svg";
import { Navigate, NavigateFunction } from "react-router-dom";
import {
  CustomButton,
  CustomLabel,
  FlexContainer,
  Loader,
  RedButton,
  SapphireBlueButton,
  ToastService,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import { MerchantAccount, SelectedItemModel } from "../../../shared/models";
import { ApplicationStatus } from "../../../shared/enums";
import { IAPIDeleteMerchantAccount } from "../../../shared/interfaces";
import { AuthService, MerchantAccountService } from "../../../shared/services";
import { ItemsStore, RolesAndPermissionStore } from "../../../shared/store";
import { AccountSettingsStore } from "../../../shared/store";
import DeleteMerchantAccountPopUp from "./DeleteMerchantAccountPopUp";

type IProps = {
  merchantAccountStore: MerchantAccountStore;
  navigate: NavigateFunction;
  rolesAndPermissionStore: RolesAndPermissionStore;
  itemStore: ItemsStore;
};
interface MenuItem {
  label: string;
  action: (item: MerchantAccount) => void;
}

const MerchantAccounts: React.FC<IProps> = ({
  merchantAccountStore,
  navigate,
  rolesAndPermissionStore,
  itemStore,
}) => {
  const [showDeletePopup, setShowDeletePopup] = React.useState(false);
  const [merchantAccountToDelete, setMerchantAccountToDelete] =
    React.useState<MerchantAccount | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await merchantAccountStore.fetchMerchantAccounts();
      setIsLoading(false);
    };

    fetchData();
  }, []);

  useEffect(() => {
    itemStore?.setBreadCrumbData(
      new SelectedItemModel({
        route: "/app/merchant-accounts",
        title: "",
      })
    );

    return () => {
      itemStore?.setBreadCrumbData();
    };
  }, []);

  const merchantAccountsData: MerchantAccount[] =
    merchantAccountStore.merchantAccounts;

  if (
    merchantAccountsData.length === 1 &&
    AuthService.hasAccount &&
    merchantAccountsData[0].applicationStatus === ApplicationStatus.Pending
  ) {
    navigate(`/app/merchant-accounts/success/${merchantAccountsData[0].id}`);
  }

  useEffect(() => {
    merchantAccountStore.fetchMerchantAccounts();
  }, []);

  const getMenuItems = (applicationStatus: string): MenuItem[] => {
    const commonItem: MenuItem[] = [
      {
        label: "Delete Account",
        action: (item: MerchantAccount) => handleDelete(item),
      },
    ];
    switch (applicationStatus) {
      case ApplicationStatus.Pending:
        return [
          {
            label: "Complete Application",
            action: (item: MerchantAccount) => handleCompleteApplication(item),
          },
          {
            label: "Cancel Application",
            action: (item: MerchantAccount) =>
              handleMerchantAccountAction(item, "cancel"),
          },
        ];
      case ApplicationStatus.Active:
        return [
          {
            label: "Make Inactive",
            action: (item: MerchantAccount) => handleInactiveApplication(item),
          },
          ...commonItem,
        ];
      case ApplicationStatus.Inactive:
        return [
          {
            label: "Make Active",
            action: (item: MerchantAccount) => handleActiveApplication(item),
          },
          ...commonItem,
        ];
      default:
        return commonItem;
    }
  };

  const handleDelete = (item: MerchantAccount) => {
    setShowDeletePopup(true);
    setMerchantAccountToDelete(item);
  };

  const onCloseDeletePopUp = (deleted: boolean) => {
    setShowDeletePopup(false);
    setMerchantAccountToDelete(null);
    if (deleted) {
      merchantAccountStore.fetchMerchantAccounts();
    }
  };

  const handleCompleteApplication = (item: MerchantAccount) => {
    if (
      !rolesAndPermissionStore.hasManagePermission("Complete the Application")
    )
      return;
    merchantAccountStore.fetchCompleteApplicationLink(item.applicationId);
  };

  const handleInactiveApplication = (item: MerchantAccount) => {
    if (!rolesAndPermissionStore.hasManagePermission("Edit the Application"))
      return;
    merchantAccountStore.updateApplicationState(
      item.id,
      ApplicationStatus.Inactive
    );
  };

  const handleActiveApplication = (item: MerchantAccount) => {
    if (!rolesAndPermissionStore.hasManagePermission("edit the application"))
      return;
    merchantAccountStore.updateApplicationState(
      item.id,
      ApplicationStatus.Active
    );
  };

  const handleMerchantAccountAction = (
    item: MerchantAccount,
    actionType: "delete" | "cancel"
  ) => {
    if (
      !rolesAndPermissionStore.hasManagePermission(
        actionType === "cancel"
          ? "Cancel the application"
          : "Delete the application"
      )
    )
      return;
    const data = item.createAPIModel2();
    const apiModel: IAPIDeleteMerchantAccount = {
      accountId: AuthService.authData?.accountId || "",
      merchantAccount: data,
    };

    const actionService =
      actionType === "delete"
        ? MerchantAccountService.deleteMerchantAccount(apiModel)
        : MerchantAccountService.cancelMerchantAccount(apiModel);

    actionService.subscribe({
      next: (result) => {
        if (!result.result) {
          const errorMessage =
            actionType === "delete"
              ? "Error in deleting merchant account"
              : "Error in cancelling application";
          ToastService.showError(errorMessage);
        } else {
          merchantAccountStore.fetchMerchantAccounts();
          const successMessage =
            actionType === "delete"
              ? "Merchant Account deleted successfully"
              : "Application cancelled successfully";
          ToastService.showSuccess(successMessage);
        }
      },
      error: () => {
        const errorMessage =
          actionType === "delete"
            ? "Error in deleting merchant account"
            : "Error in cancelling application";
        ToastService.showError(errorMessage);
      },
    });
  };

  const getStatusStyles = (status: ApplicationStatus) => {
    switch (status) {
      case ApplicationStatus.Pending:
        return { backgroundColor: "#FFF5F8", color: "#DE5F85" };
      case ApplicationStatus.Active:
        return { backgroundColor: "#EEF7EA", color: "#2ECC71" };
      case ApplicationStatus.Inactive:
        return { backgroundColor: "#F9F9F9", color: "#95A5A6" };
      case "InReview" as keyof typeof ApplicationStatus:
        return { backgroundColor: "#ECF0FF", color: "#6A7AB5" };
      case ApplicationStatus.Cancelled:
        return { backgroundColor: "#F9F9F9", color: "#95A5A6" };
      default:
        return { backgroundColor: "#EEEEEE", color: "#555555" };
    }
  };

  const renderBadge = (
    statusStyles: { backgroundColor: string; color: string },
    applicationStatus: ApplicationStatus
  ) => (
    <CustomButton
      bgColor={statusStyles.backgroundColor}
      color={statusStyles.color}
      fontWeight="600 !important"
    >
      {ApplicationStatus[applicationStatus as keyof typeof ApplicationStatus]}
    </CustomButton>
  );

  const renderDropdown = (item: MerchantAccount) => (
    <Dropdown align="end" className="merchant-status-button">
      <Dropdown.Toggle as="div" className="ellipsis-vertical">
        <img src={ellipsisVertical} />
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {getMenuItems(item.applicationStatus).map((menuItem, menuIndex) => (
          <Dropdown.Item key={menuIndex} onClick={() => menuItem.action(item)}>
            {menuItem.label}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );

  const onClickCardBody = (id: string) => {
    navigate(`/app/merchant-accounts/details/${id}`);
  };

  const renderCardBody = (
    item: MerchantAccount,
    statusStyles: { backgroundColor: string; color: string }
  ) => (
    <>
      <FlexContainer className="merchant-card-header">
        {renderBadge(statusStyles, item.applicationStatus)}
        <img src={MindMePaymentsLogo} />
        {renderDropdown(item)}
      </FlexContainer>
      <FlexContainer fxLayout="column" alignItems="center" spacing={20}>
        <FlexContainer
          fxLayout="column"
          alignItems="center"
          spacing={20}
          onClick={() => onClickCardBody(item.id)}
        >
          <CustomLabel textAlign="center" fontSize={"18px"} fontWeight="600">
            {item.name}
          </CustomLabel>
          <CustomLabel
            textAlign="center"
            fontSize={"14px"}
            className="merchant-card-descr"
          >
            {item.applicationStatus === ApplicationStatus.Active ||
            item.applicationStatus === ApplicationStatus.InReview ? (
              <>
                Your merchant account is approved and active.
                <br />
                You are able to process payments.
              </>
            ) : (
              <>
                Open the link below, complete all required information, sign
                electronically and submit.
              </>
            )}
          </CustomLabel>
        </FlexContainer>
        {!(item.applicationStatus === ApplicationStatus.Pending) && (
          <CustomLabel>
            <span className="semi-bold">
              ${AccountSettingsStore.formatAmount(item.amountProcessed)}
            </span>
            <span className="charcoal-label"> processed since</span>
            <span className="semi-bold">
              {" "}
              {UtilityMethods.formatDateInString(new Date(item.createdDate))}
            </span>
          </CustomLabel>
        )}
        {item.applicationStatus === ApplicationStatus.Pending && (
          <FlexContainer
            justifyContent="center"
            alignItems="center"
            spacing={20}
            padding="5px 0px 0px"
          >
            <RedButton
              height="33px !important"
              onClick={() => handleMerchantAccountAction(item, "cancel")}
            >
              Cancel Application
            </RedButton>
            <SapphireBlueButton
              onClick={() => handleCompleteApplication(item)}
              height="33px !important"
            >
              Complete Application
            </SapphireBlueButton>
          </FlexContainer>
        )}
      </FlexContainer>
    </>
  );

  if (merchantAccountStore.isMerchantAccountsLoadingInProgress) {
    return <>{new Loader(true).loaderComponent}</>;
  }
  if (merchantAccountsData.length === 0 && !isLoading) {
    return <Navigate to="/app/merchant-accounts/no-merchant" />;
  }
  return (
    <FlexContainer
      fxWrap="wrap"
      spacing={30}
      className={`${merchantAccountsData.length === 0 ? "full-height" : ""}`}
    >
      {showDeletePopup && merchantAccountToDelete && (
        <DeleteMerchantAccountPopUp
          onClose={(refresh: boolean) => onCloseDeletePopUp(refresh)}
          merchantAccount={merchantAccountToDelete}
        />
      )}
      {merchantAccountsData.map((item, index) => {
        const statusStyles = getStatusStyles(
          item.applicationStatus as ApplicationStatus
        );
        return (
          <MerchantCard key={index}>
            {renderCardBody(item, statusStyles)}
          </MerchantCard>
        );
      })}
    </FlexContainer>
  );
};

export default withRouter(observer(MerchantAccounts));
